import { X, Mail, Phone, Building, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { Database } from '@/types/database';

type Contact = Database['public']['Tables']['contact']['Row'];

interface Props {
  contact: Contact;
  onClose: () => void;
}

export function ContactDetailPanel({ contact, onClose }: Props) {
  const initials = contact.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="h-14 w-14">
            <AvatarImage src={contact.avatar_url ?? undefined} />
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-lg font-semibold">{contact.name}</h3>
            {contact.title && (
              <p className="text-sm text-muted-foreground">{contact.title}</p>
            )}
            <Badge variant="outline" className="mt-1 text-xs">
              {contact.source === 'manual' ? 'External' : contact.source}
            </Badge>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Separator />

      {/* Contact Info */}
      <div className="space-y-3">
        {contact.email && (
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
              {contact.email}
            </a>
          </div>
        )}
        {contact.phone && (
          <div className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <a href={`tel:${contact.phone}`} className="hover:underline">
              {contact.phone}
            </a>
          </div>
        )}
        {contact.company && (
          <div className="flex items-center gap-2 text-sm">
            <Building className="h-4 w-4 text-muted-foreground" />
            {contact.company}
          </div>
        )}
        {contact.department && (
          <div className="flex items-center gap-2 text-sm">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            {contact.department}
          </div>
        )}
      </div>

      {/* Notes */}
      {contact.notes && (
        <>
          <Separator />
          <div>
            <div className="text-sm font-medium mb-1">Notes</div>
            <p className="text-sm text-muted-foreground">{contact.notes}</p>
          </div>
        </>
      )}

      <Separator />

      {/* Quick Actions */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">Quick Actions</h4>
        {contact.email && (
          <Button variant="outline" size="sm" className="w-full" asChild>
            <a href={`mailto:${contact.email}`}>
              <Mail className="mr-2 h-4 w-4" /> Send Email
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
